"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Date = exports.DayOfWeek = void 0;
exports.LastDayOfMonth = LastDayOfMonth;
exports.DaysInYear = DaysInYear;
exports.GenerateDates = GenerateDates;
exports.CommonDays = CommonDays;
const sprintf_js_1 = require("sprintf-js");
const jsdate_1 = require("./jsdate");
const timezone_1 = require("./timezone");
const time_1 = require("./time");
function LastDayOfMonth(year, month) {
    return new jsdate_1.JSDate(year, month, 0).getDate();
}
// These match JSDate.getDay()
var DayOfWeek;
(function (DayOfWeek) {
    DayOfWeek[DayOfWeek["SUNDAY"] = 0] = "SUNDAY";
    DayOfWeek[DayOfWeek["MONDAY"] = 1] = "MONDAY";
    DayOfWeek[DayOfWeek["TUESDAY"] = 2] = "TUESDAY";
    DayOfWeek[DayOfWeek["WEDNESDAY"] = 3] = "WEDNESDAY";
    DayOfWeek[DayOfWeek["THURSDAY"] = 4] = "THURSDAY";
    DayOfWeek[DayOfWeek["FRIDAY"] = 5] = "FRIDAY";
    DayOfWeek[DayOfWeek["SATURDAY"] = 6] = "SATURDAY";
})(DayOfWeek || (exports.DayOfWeek = DayOfWeek = {}));
class Date {
    // Each insance is frozen to ensure that values are not changed when
    // reference multiple times.
    constructor(year, month, day) {
        this.year = year;
        this.month = month;
        this.day = day;
        Object.freeze(this);
    }
    toString(mmddyy = false) {
        if (mmddyy) {
            return (0, sprintf_js_1.sprintf)("%02d/%02d/%04d", this.month, this.day, this.year);
        }
        return (0, sprintf_js_1.sprintf)("%04d-%02d-%02d", this.year, this.month, this.day);
    }
    dayOfWeek() {
        const js_date = new jsdate_1.JSDate(this.year, this.month - 1, this.day);
        return js_date.getDay();
    }
    isWeekend() {
        const day = this.dayOfWeek();
        return day === DayOfWeek.SATURDAY || day === DayOfWeek.SUNDAY;
    }
    toJSON() {
        return {
            year: this.year,
            month: this.month,
            day: this.day,
        };
    }
    static fromJSON(json) {
        return new Date(json.year, json.month, json.day);
    }
    // This is the undefined state.
    static zero() {
        return Date.fromString("0000-00-00");
    }
    isLastDayOfMonth() {
        return LastDayOfMonth(this.year, this.month) === this.day;
    }
    isLastDayOfQuarter() {
        const days = [
            new Date(this.year, 4, 1).decrement(),
            new Date(this.year, 7, 1).decrement(),
            new Date(this.year, 10, 1).decrement(),
            new Date(this.year + 1, 1, 1).decrement(),
        ];
        for (let i = 0; i < days.length; i++) {
            if (this.eq(days[i])) {
                return true;
            }
        }
        return false;
    }
    isLastDayOfYear() {
        return this.eq(new Date(this.year, 12, 31));
    }
    static fromTimestamp(timestamp_ms) {
        const d = new jsdate_1.JSDate(timestamp_ms);
        return new Date(d.getFullYear(), d.getMonth() + 1, d.getDate());
    }
    static fromString(date_str) {
        const [year, month, day] = date_str.split("-");
        return new Date(parseInt(year, 10), parseInt(month, 10), parseInt(day, 10));
    }
    // For testing
    static setToday(today) {
        if (!(0, time_1.IsTest)()) {
            throw new Error("Must be run in test");
        }
        this.TODAY = today;
    }
    static fromStartEnd(date_from, date_to) {
        const dates = [];
        for (let date = date_from; date.lte(date_to); date = date.increment()) {
            dates.push(date);
        }
        return dates;
    }
    static today(time_zone = timezone_1.TimeZone.PST) {
        if ((0, time_1.IsTest)() && this.TODAY !== null) {
            return this.TODAY;
        }
        const d = (0, timezone_1.GetLocalDate)(time_zone);
        return new Date(d.year, d.month, d.day);
    }
    incrYear() {
        return new Date(this.year + 1, this.month, Math.min(this.day, LastDayOfMonth(this.year + 1, this.month)));
    }
    decrYear() {
        return new Date(this.year - 1, this.month, Math.min(this.day, LastDayOfMonth(this.year - 1, this.month)));
    }
    addYear(num) {
        const year = this.year + num;
        return new Date(year, this.month, Math.min(this.day, LastDayOfMonth(year, this.month)));
    }
    incrMonth() {
        let { day, month, year } = this;
        month++;
        if (month === 13) {
            year++;
            month = 1;
        }
        return new Date(year, month, Math.min(day, LastDayOfMonth(year, month)));
    }
    decrMonth() {
        let { day, month, year } = this;
        month--;
        if (month === 0) {
            month = 12;
            year--;
        }
        return new Date(year, month, Math.min(day, LastDayOfMonth(year, month)));
    }
    addMonth(num) {
        let date = this.copy();
        const day = date.day;
        for (let i = 0; i < Math.abs(num); i++) {
            if (num > 0) {
                date = date.incrMonth();
            }
            else {
                date = date.decrMonth();
            }
        }
        return new Date(date.year, date.month, Math.min(day, LastDayOfMonth(date.year, date.month)));
    }
    // Add one day
    increment() {
        let { day, month, year } = this;
        day++;
        if (day > LastDayOfMonth(year, month)) {
            day = 1;
            month++;
            if (month === 13) {
                month = 1;
                year++;
            }
        }
        return new Date(year, month, day);
    }
    addDay(num_days) {
        let result = this.copy();
        if (num_days > 0) {
            for (let i = 0; i < num_days; i++) {
                result = result.increment();
            }
        }
        else {
            for (let i = 0; i < -num_days; i++) {
                result = result.decrement();
            }
        }
        return result;
    }
    decrement() {
        let { day, month, year } = this;
        day--;
        if (day === 0 && month === 1) {
            // Set to last day of last year
            year--;
            month = 12;
            day = LastDayOfMonth(year, month);
        }
        else if (day === 0) {
            month--;
            day = LastDayOfMonth(year, month);
        }
        return new Date(year, month, day);
    }
    copy() {
        return new Date(this.year, this.month, this.day);
    }
    cmp(date) {
        if (this.gt(date)) {
            return 1;
        }
        if (this.lt(date)) {
            return -1;
        }
        return 0;
    }
    lt(date) {
        if (this.year !== date.year) {
            return this.year < date.year;
        }
        if (this.month !== date.month) {
            return this.month < date.month;
        }
        return this.day < date.day;
    }
    gt(date) {
        return !this.lt(date) && !this.eq(date);
    }
    lte(date) {
        return this.lt(date) || this.eq(date);
    }
    gte(date) {
        return this.gt(date) || this.eq(date);
    }
    eq(date) {
        return this.year === date.year
            && this.month === date.month && this.day === date.day;
    }
    // Number of days
    diffDays(date) {
        if (this.eq(date)) {
            return 0;
        }
        let from, to;
        let mult = 1;
        if (this.lt(date)) {
            from = this.copy();
            to = date;
        }
        else {
            from = date.copy();
            to = this;
            mult = -1;
        }
        let count = 0;
        while (from.lt(to)) {
            from = from.increment();
            count++;
        }
        return count * mult;
    }
    /**
     * Returns a random date between from_date and to_date
     */
    static rnd_date(from_date, to_date) {
        const total_days = from_date.diffDays(to_date) + 1;
        const num_days = Math.floor(Math.random() * total_days);
        return from_date.addDay(num_days);
    }
    static min(...dates) {
        let result = dates[0];
        for (let i = 1; i < dates.length; i++) {
            result = result.lt(dates[i]) ? result : dates[i];
        }
        return result;
    }
    static max(...dates) {
        let result = dates[0];
        for (let i = 1; i < dates.length; i++) {
            result = result.gt(dates[i]) ? result : dates[i];
        }
        return result;
    }
}
exports.Date = Date;
// For test
Date.TODAY = null;
function DaysInYear(year) {
    let count = 0;
    for (let date = new Date(year, 1, 1); date.lt(new Date(year + 1, 1, 1)); date = date.increment()) {
        count++;
    }
    return count;
}
function GenerateDates(from_date, to_date) {
    const dates = [];
    for (let date = from_date; date.lte(to_date); date = date.increment()) {
        dates.push(date);
    }
    return dates;
}
/**
 * Return an array of all dates in common between dates_a and dates_b. Both arrays
 * should be sorted.
 */
function CommonDays(dates_a, dates_b) {
    const result = [];
    const set_a = new Set(dates_a.map((date) => date.toString()));
    const set_b = new Set(dates_b.map((date) => date.toString()));
    const from_date = Date.min(dates_a[0], dates_b[0]);
    const to_date = Date.max(dates_a[dates_a.length - 1], dates_b[dates_b.length - 1]);
    for (let date = from_date; date.lte(to_date); date = date.increment()) {
        if (set_a.has(date.toString()) && set_b.has(date.toString())) {
            result.push(date);
        }
    }
    return result;
}
